import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Gauge, TrendingDown } from "lucide-react";
import { fmtIDR, fmtNum, FUEL_META, todayISO } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";

const Rekonsiliasi = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ fuel_code: "pertalite", date: todayISO(), dip_stick_liters: "", meter_totalizer_liters: "", note: "" });

  const load = () => api.get("/reconciliations").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reconciliations", {
        ...form,
        dip_stick_liters: parseFloat(form.dip_stick_liters),
        meter_totalizer_liters: form.meter_totalizer_liters ? parseFloat(form.meter_totalizer_liters) : null,
      });
      toast.success("Rekonsiliasi tersimpan");
      setOpen(false); load();
      setForm({ fuel_code: "pertalite", date: todayISO(), dip_stick_liters: "", meter_totalizer_liters: "", note: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus rekonsiliasi ini?")) return;
    await api.delete(`/reconciliations/${id}`); toast.success("Terhapus"); load();
  };

  const totalLoss = rows.reduce((s, r) => s + (r.loss_value || 0), 0);
  const totalLossLiters = rows.reduce((s, r) => s + (r.loss_liters || 0), 0);

  return (
    <div className="space-y-6" data-testid="rekonsiliasi-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900" data-testid="rekonsiliasi-title">Rekonsiliasi Tangki</h1>
          <p className="text-sm text-slate-500 mt-1">Dip stick vs stock buku — deteksi susut / losses BBM otomatis</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="rekon-open-dialog" className="bg-[#E30613] hover:bg-[#C8040F]"><Plus className="w-4 h-4 mr-2" />Input Dip Stick</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Rekonsiliasi Harian Tangki</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div><Label>Jenis BBM</Label>
                <Select value={form.fuel_code} onValueChange={(v) => setForm(f => ({ ...f, fuel_code: v }))}>
                  <SelectTrigger data-testid="rekon-fuel-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(FUEL_META).map(([code, m]) => <SelectItem key={code} value={code} data-testid={`rekon-fuel-${code}`}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Tanggal</Label><Input data-testid="rekon-date" type="date" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} required /></div>
                <div><Label>Dip Stick (L)</Label><Input data-testid="rekon-dip-stick" type="number" step="0.01" value={form.dip_stick_liters} onChange={(e) => setForm(f => ({ ...f, dip_stick_liters: e.target.value }))} required /></div>
              </div>
              <div><Label>Totalizer Meter Dispenser (L, opsional)</Label>
                <Input data-testid="rekon-totalizer" type="number" step="0.01" value={form.meter_totalizer_liters} onChange={(e) => setForm(f => ({ ...f, meter_totalizer_liters: e.target.value }))} />
              </div>
              <div><Label>Catatan</Label><Input data-testid="rekon-note" value={form.note} onChange={(e) => setForm(f => ({ ...f, note: e.target.value }))} /></div>
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                Selisih akan dihitung otomatis. Jika dip stick lebih kecil dari stock buku, selisih akan dicatat sebagai <b>Beban Susut BBM</b> di jurnal.
              </div>
              <DialogFooter><Button type="submit" data-testid="rekon-submit" className="bg-[#E30613] hover:bg-[#C8040F]">Simpan Rekonsiliasi</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-rose-50 to-white border-rose-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-rose-600 font-semibold">Total Susut Nilai</div>
                <div className="text-2xl font-extrabold font-mono-fig text-rose-700 mt-2" data-testid="rekon-total-loss-value">{fmtIDR(totalLoss)}</div>
              </div>
              <TrendingDown className="w-8 h-8 text-rose-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Total Susut Liter</div>
            <div className="text-2xl font-extrabold font-mono-fig mt-2">{fmtNum(totalLossLiters)} L</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Jumlah Pengukuran</div>
            <div className="text-2xl font-extrabold font-mono-fig mt-2">{rows.length}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg font-bold flex items-center gap-2"><Gauge className="w-5 h-5" />Riwayat Rekonsiliasi</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tanggal</TableHead>
                <TableHead>BBM</TableHead>
                <TableHead className="text-right">Dip Stick (L)</TableHead>
                <TableHead className="text-right">Stock Buku (L)</TableHead>
                <TableHead className="text-right">Selisih (L)</TableHead>
                <TableHead className="text-right">Nilai Susut</TableHead>
                <TableHead>Operator</TableHead>
                {user?.role === "admin" && <TableHead></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const m = FUEL_META[r.fuel_code];
                const isLoss = r.variance_liters < -0.01;
                const isGain = r.variance_liters > 0.01;
                return (
                  <TableRow key={r.id} data-testid={`rekon-row-${r.id}`}>
                    <TableCell className="font-mono-fig text-sm">{r.date}</TableCell>
                    <TableCell><Badge className={`${m.bg} ${m.text} border ${m.border}`}>{m.name}</Badge></TableCell>
                    <TableCell className="text-right font-mono-fig" data-testid={`rekon-dip-${r.id}`}>{fmtNum(r.dip_stick_liters)}</TableCell>
                    <TableCell className="text-right font-mono-fig" data-testid={`rekon-book-${r.id}`}>{fmtNum(r.book_stock_liters)}</TableCell>
                    <TableCell data-testid={`rekon-variance-${r.id}`} className={`text-right font-mono-fig font-bold ${isLoss ? "text-rose-700" : isGain ? "text-emerald-700" : "text-slate-500"}`}>
                      {r.variance_liters > 0 ? "+" : ""}{fmtNum(r.variance_liters)}
                    </TableCell>
                    <TableCell className="text-right font-mono-fig text-rose-700" data-testid={`rekon-loss-value-${r.id}`}>{r.loss_value > 0 ? fmtIDR(r.loss_value) : "-"}</TableCell>
                    <TableCell className="text-sm">{r.operator_name}</TableCell>
                    {user?.role === "admin" && <TableCell><Button size="icon" variant="ghost" data-testid={`rekon-delete-${r.id}`} onClick={() => del(r.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button></TableCell>}
                  </TableRow>
                );
              })}
              {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-8">Belum ada pengukuran</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Rekonsiliasi;
